import React, {useEffect, useState} from 'react'
import moment from 'moment-timezone'
import {OwnerState} from '../../store/owner/types'
import {EumYang} from '../../enums/EumYang'
import {getDaysOfLunarMonth, hasLeapMonth} from '../../functions/lunar/utils'
import {checkSummerTime, getOrientalTime} from "./owner-input-box";

interface Props {
  owner: OwnerState
  onSexChanged: (sex: EumYang) => void
  onCalendarChanged: (calendar: EumYang) => void
  onYearChanged: (year: string) => void
  onMonthChanged: (month: string) => void
  onDayChanged: (day: string) => void
  onHourChanged: (hour: string) => void
  onMinuteChanged: (minute: string) => void
  onIsLeapMonthChanged: (isLeapMonth: boolean) => void
}

// 사용자 생년월일시를 입력하는 Input Box
const InputBox: React.FC<Props> = (props) => {
  const {
    sex,
    calendar,
    year,
    month,
    day,
    hour,
    minute,
    isLeapMonth,
  } = props.owner
  const {
    onCalendarChanged,
    onDayChanged,
    onHourChanged,
    onMinuteChanged,
    onMonthChanged,
    onSexChanged,
    onYearChanged,
    onIsLeapMonthChanged,
  } = props
  const years: number[] = []
  const curYear = parseInt(moment().tz('Asia/Seoul').format('YYYY'))
  for (let i = 1901; i <= curYear; i++) {
    years.push(i)
  }
  const months: number[] = []

  for (let i = 1; i <= 12; i++) {
    months.push(i)
  }

  const [days, setDays] = useState(
    getPossibleDays(year, month, calendar, isLeapMonth),
  )
  const [gender, setGender] = useState(EumYang.YANG)
  const [calendarType, setCalendarType] = useState(EumYang.YANG)
  const [summerTime, setSummerTime] = useState(checkSummerTime(year, month, day, hour, minute, calendar, isLeapMonth));
  const [orientalTime, setOrientalTime] = useState(getOrientalTime(year, month, day, hour, minute, calendar, isLeapMonth))

  useEffect(() => {
    setDays(getPossibleDays(year, month, calendar, isLeapMonth))
  }, [year, month, calendar, isLeapMonth])

  useEffect(() => {
    setGender(sex)
  }, [sex])

  useEffect(() => {
    setCalendarType(calendar)
  }, [calendar])

  useEffect(() => {
    setSummerTime(checkSummerTime(year, month, day, hour, minute, calendar, isLeapMonth));
    setOrientalTime(getOrientalTime(year, month, day, hour, minute, calendar, isLeapMonth));
  }, [year, month, day, calendar, isLeapMonth, hour, minute]);

  return (
    <div
      className=" max-w-md ring-1 shadow ring-gray-200 bg-white  rounded px-2 sm:px-4 pt-6 pb-8 mb-4 my-2 text-left mx-auto ">
      <div className="flex ">
        <div className="px-3 mb-6">
          <label className="block tracking-wide text-gray-500 text-xs font-bold mb-2">
            성별
          </label>
          <div className="flex">
            <div
              onClick={() => onSexChanged(EumYang.YANG)}
              className={`text-sm sm:text-lg cursor-pointer mr-4 ${
                gender === EumYang.YANG
                  ? 'bold underline text-gray-800'
                  : 'text-gray-400'
                }`}
            >
              남성
            </div>
            <div
              onClick={() => onSexChanged(EumYang.EUM)}
              className={`text-sm sm:text-lg cursor-pointer mr-4 ${
                gender === EumYang.EUM
                  ? 'bold underline text-gray-800'
                  : 'text-gray-400'
                }`}
            >
              여성
            </div>
          </div>
        </div>
        <div className="px-3">
          <label className="block tracking-wide text-gray-500 text-xs font-bold mb-2">
            역법
          </label>
          <div className="flex">
            <div
              onClick={() => {
                onCalendarChanged(EumYang.YANG)
                onIsLeapMonthChanged(false)
              }}
              className={`text-sm sm:text-lg cursor-pointer mr-4 ${
                calendarType === EumYang.YANG
                  ? 'bold underline text-gray-800'
                  : 'text-gray-400'
                }`}
            >
              양력
            </div>
            <div
              onClick={() => {
                onCalendarChanged(EumYang.EUM)
                onIsLeapMonthChanged(false)
              }}
              className={`text-sm sm:text-lg cursor-pointer mr-4 ${
                calendarType === EumYang.EUM
                  ? 'bold underline text-gray-800'
                  : 'text-gray-400'
                }`}
            >
              음력
            </div>
          </div>
        </div>
      </div>
      <div className="sm:flex">
        <div className="px-3 mb-6">
          <label className="block tracking-wide text-gray-500 text-xs font-bold mb-2">
            태어난 해
          </label>
          <div className="relative w-28">
            <select
              className="block appearance-none w-full bg-grey-lighter border border-grey-lighter text-grey-darker py-2 px-4 pr-8 rounded"
              value={year}
              onChange={(event) => {
                onYearChanged(event.target.value)
                onIsLeapMonthChanged(false)
              }}
            >
              {years.map((val, index) => (
                <option key={index} value={val}>
                  {val}년
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute right-0 inset-y-0 flex items-center px-2 text-gray-darker">
              <svg
                className="h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
              >
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
              </svg>
            </div>
          </div>
        </div>
        <div className="flex">
          <div className="px-3 mb-6">
            <label className="block tracking-wide text-gray-500 text-xs font-bold mb-2">
              태어난 달
            </label>

            <div className="relative w-28">
              <select
                className="block appearance-none w-full bg-grey-lighter border border-grey-lighter text-grey-darker py-2 px-4 pr-8 rounded"
                value={month}
                onChange={(event) => {
                  onMonthChanged(event.target.value)
                  onIsLeapMonthChanged(false)
                }}
              >
                {months.map((val, index) => (
                  <option key={index} value={val}>
                    {val}월
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute right-0 inset-y-0 flex items-center px-2 text-gray-darker">
                <svg
                  className="h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                </svg>
              </div>
            </div>
            {
              calendar === EumYang.EUM && hasLeapMonth(year, month) &&
              <div className="flex items-center mt-1 justify-end">
                <div className="text-sm bold text-gray-600">윤달</div>
                <input className="w-4 h-4 ml-2"
                       type="checkbox" checked={isLeapMonth}
                       onChange={event => {
                         onIsLeapMonthChanged(event.target.checked);
                       }}
                />
              </div>
            }

          </div>
          <div className="px-3">
            <label className="block tracking-wide text-gray-500 text-xs font-bold mb-2">
              태어난 날
            </label>
            <div className="relative w-28">
              <select
                className="block appearance-none w-full bg-grey-lighter border border-grey-lighter text-grey-darker py-2 px-4 pr-8 rounded"
                value={day}
                onChange={(event) => {
                  onDayChanged(event.target.value)
                }}
              >
                {days.map((val, index) => (
                  <option key={index} value={val}>
                    {val}일
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute right-0 inset-y-0 flex items-center px-2 text-gray-darker">
                <svg
                  className="h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex  mb-0">
        <div className="px-3">
          <label className="block tracking-wide text-gray-500 text-xs font-bold mb-2">
            태어난 시
          </label>
          <div className="relative w-28">
            <select
              className="block appearance-none w-full bg-grey-lighter border border-grey-lighter text-grey-darker py-2 px-2 rounded"
              value={hour}
              onChange={(event) => {
                onHourChanged(event.target.value)
              }}
            >
              <option key={0} value={0}>
                밤 12시
              </option>
              <option key={1} value={1}>
                새벽 1시
              </option>
              <option key={2} value={2}>
                새벽 2시
              </option>
              <option key={3} value={3}>
                새벽 3시
              </option>
              <option key={4} value={4}>
                새벽 4시
              </option>
              <option key={5} value={5}>
                새벽 5시
              </option>
              <option key={6} value={6}>
                아침 6시
              </option>
              <option key={7} value={7}>
                아침 7시
              </option>
              <option key={8} value={8}>
                아침 8시
              </option>
              <option key={9} value={9}>
                아침 9시
              </option>
              <option key={10} value={10}>
                아침 10시
              </option>
              <option key={11} value={11}>
                낮 11시
              </option>
              <option key={12} value={12}>
                낮 12시
              </option>
              <option key={13} value={13}>
                낮 1시
              </option>
              <option key={14} value={14}>
                낮 2시
              </option>
              <option key={15} value={15}>
                낮 3시
              </option>
              <option key={16} value={16}>
                낮 4시
              </option>
              <option key={17} value={17}>
                저녁 5시
              </option>
              <option key={18} value={18}>
                저녁 6시
              </option>
              <option key={19} value={19}>
                저녁 7시
              </option>
              <option key={20} value={20}>
                밤 8시
              </option>
              <option key={21} value={21}>
                밤 9시
              </option>
              <option key={22} value={22}>
                밤 10시
              </option>
              <option key={23} value={23}>
                밤 11시
              </option>
            </select>
            <div className="pointer-events-none absolute right-0 inset-y-0 flex items-center px-2 text-gray-darker">
              <svg
                className="h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
              >
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
              </svg>
            </div>
          </div>
        </div>
        <div className="px-3">
          <label className="block tracking-wide text-gray-500 text-xs font-bold mb-2">
            태어난 분
          </label>
          <div className="relative w-28">
            <select
              className="block appearance-none w-full bg-grey-lighter border border-grey-lighter text-grey-darker py-2 px-4 pr-8 rounded"
              value={minute}
              onChange={(event) => {
                onMinuteChanged(event.target.value)
              }}
            >
              {[
                0,
                1,
                2,
                3,
                4,
                5,
                6,
                7,
                8,
                9,
                10,
                11,
                12,
                13,
                14,
                15,
                16,
                17,
                18,
                19,
                20,
                21,
                22,
                23,
                24,
                25,
                26,
                27,
                28,
                29,
                30,
                31,
                32,
                33,
                34,
                35,
                36,
                37,
                38,
                39,
                40,
                41,
                42,
                43,
                44,
                45,
                46,
                47,
                48,
                49,
                50,
                51,
                52,
                53,
                54,
                55,
                56,
                57,
                58,
                59,
              ].map((val, index) => (
                <option key={index} value={val}>
                  {val}분
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute right-0 inset-y-0 flex items-center px-2 text-gray-darker">
              <svg
                className="h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
              >
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
              </svg>
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-end w-64">
        {
          summerTime &&
          <div className="text-xs text-gray-500 font-bold">
            써머타임
          </div>
        }

        <div className="text-xs ml-4 text-gray-500 font-bold">
          {orientalTime}
        </div>
      </div>
    </div>
  )
}

export default InputBox

function getDaysOfMonth(
  year: number,
  month: number,
  calendar: EumYang,
  isLeapMonth: boolean,
): number {
  if (calendar === EumYang.YANG) {
    if ([1, 3, 5, 7, 8, 10, 12].includes(month)) {
      return 31
    }
    if (month === 2 && year % 4 === 0) {
      return 29
    }
    if (month === 2) {
      return 28
    }
  } else {
    return getDaysOfLunarMonth(year, month, isLeapMonth)
  }
  return 30
}

function getPossibleDays(
  yearString: string,
  monthString: string,
  calendar: EumYang,
  isLeapMonth: boolean,
): number[] {
  const year = parseInt(yearString)
  const month = parseInt(monthString)
  const lastDay = getDaysOfMonth(year, month, calendar, isLeapMonth)
  const days: number[] = []
  for (let i = 1; i <= lastDay; i++) {
    days.push(i)
  }
  return days
}
